import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useAppToast } from '../hooks/useAppToast';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { PROJECT_STORAGE_KEY } from '../lib/scopeHeaders';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  active: boolean;
}

export interface Project {
  id: string;
  name: string;
  isDefault: boolean;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  role: string;
  roleLabel: string;
}

interface TenantContextType {
  tenants: Tenant[];
  /** Organizações das quais o usuário é membro (seletor da barra superior) */
  organizations: Organization[];
  /** Papel na organização ativa (OWNER, ADMIN, BILLING_ADMIN, MEMBER, READ_ONLY ou SUPERADMIN) */
  role: string | null;
  roleLabel: string | null;
  /** Permissões na organização ativa — use can() para esconder ações */
  can: (permission: string) => boolean;
  selectedTenantId: string;
  selectedTenant: Tenant | null;
  setSelectedTenantId: (id: string) => void;
  loading: boolean;
  refreshTenants: () => Promise<void>;
  /** Projetos da organização ativa e o projeto ativo (Fase B) */
  projects: Project[];
  selectedProjectId: string;
  selectedProject: Project | null;
  setSelectedProjectId: (id: string) => void;
  /** Recarrega papel, permissões e projetos (ex.: depois de criar um projeto) */
  reloadContext: () => Promise<void>;
  /** A organização exige 2FA e o usuário ainda não configurou */
  mfaSetupRequired: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [role, setRole] = useState<string | null>(null);
  const [roleLabel, setRoleLabel] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[] | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [selectedProjectId, setSelectedProjectIdState] = useState<string>(() => {
    try { return localStorage.getItem(PROJECT_STORAGE_KEY) || ''; } catch { return ''; }
  });
  const { user } = useAuth();
  const toast = useAppToast();

  // Helper autenticado (canónico, inclui X-Tenant-Id automaticamente)
  const authenticatedFetch = useAuthenticatedFetch();

  // Papel, permissões e organizações do usuário na organização ativa (RBAC)
  const loadContext = async () => {
    try {
      const response = await authenticatedFetch('/api/user-tenants/context');
      if (!response.ok) return null;
      const data = await response.json();
      setOrganizations(data.organizations || []);
      setRole(data.role ?? null);
      setRoleLabel(data.roleLabel ?? null);
      setPermissions(data.permissions || []);
      setMfaSetupRequired(!!data.mfaSetupRequired);
      // O backend valida o projeto salvo e devolve o que ficou ativo
      setProjects(data.projects || []);
      if (data.activeProjectId) {
        setSelectedProjectIdState(data.activeProjectId);
        localStorage.setItem(PROJECT_STORAGE_KEY, data.activeProjectId);
      } else {
        setSelectedProjectIdState('');
        localStorage.removeItem(PROJECT_STORAGE_KEY);
      }
      return data as { activeTenantId: string | null; organizations: Organization[] };
    } catch {
      return null;
    }
  };

  const loadTenants = async () => {
    // Usuário ainda não carregado (reload da página): não decidir nada. Antes
    // este caminho caía no ramo "sem tenant", apagava o tenant salvo e marcava
    // loading=false — as páginas buscavam dados sem X-Tenant-Id e o SUPERADMIN
    // via "Acesso negado. Usuário deve estar associado a um tenant".
    if (!user) {
      setLoading(true);
      return;
    }

    if (user.role !== 'SUPERADMIN') {
      // Membros: organização ativa = a salva (se ainda for membro) ou a "de casa".
      // O backend valida o vínculo e devolve qual ficou ativa.
      const context = await loadContext();
      const active = context?.activeTenantId || user.tenantId;
      if (context) {
        setTenants(context.organizations.map((o) => ({ id: o.id, name: o.name, slug: o.slug, active: true })));
      }
      if (active) {
        setSelectedTenantId(active);
        localStorage.setItem('selected_tenant_id', active);
      } else {
        localStorage.removeItem('selected_tenant_id');
      }
      setLoading(false);
      return;
    }

    try {
      const response = await authenticatedFetch('/api/tenants');
      if (response.ok) {
        const data = await response.json();
        const tenantsArray = data.tenants || [];
        setTenants(tenantsArray);

        // Carregar tenant selecionado do localStorage ou selecionar primeiro
        const savedTenantId = localStorage.getItem('selected_tenant_id');
        if (savedTenantId && tenantsArray.some((t: Tenant) => t.id === savedTenantId)) {
          setSelectedTenantId(savedTenantId);
        } else {
          // TenantId salvo é inválido, limpar e selecionar o primeiro
          if (savedTenantId) {
            console.warn('TenantId salvo no localStorage é inválido, limpando:', savedTenantId);
            localStorage.removeItem('selected_tenant_id');
          }

          if (tenantsArray.length > 0) {
            setSelectedTenantId(tenantsArray[0].id);
            localStorage.setItem('selected_tenant_id', tenantsArray[0].id);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const refreshTenants = async () => {
    setLoading(true);
    await loadTenants();
  };

  useEffect(() => {
    loadTenants();
  }, [user]);

  // Salvar tenant selecionado no localStorage
  useEffect(() => {
    if (selectedTenantId) {
      localStorage.setItem('selected_tenant_id', selectedTenantId);
    }
  }, [selectedTenantId]);

  // SUPERADMIN troca de organização sem recarregar: atualiza o contexto (permissões)
  useEffect(() => {
    if (user?.role === 'SUPERADMIN' && selectedTenantId) loadContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedTenantId]);

  // Se o contexto não carregou (rede, backend antigo), usa o papel global como
  // antes da Fase A — nunca deixa o admin sem acesso por falha de consulta.
  const LEGACY_ADMIN = ['org:read', 'org:update', 'members:read', 'members:manage', 'billing:read', 'integrations:manage', 'connections:manage', 'campaigns:write', 'contacts:write', 'data:read'];
  const LEGACY_USER = ['org:read', 'members:read', 'data:read', 'campaigns:write', 'contacts:write'];
  const effectivePermissions = permissions ?? (user?.role === 'ADMIN' ? LEGACY_ADMIN : LEGACY_USER);
  const can = (permission: string) => user?.role === 'SUPERADMIN' || effectivePermissions.includes(permission);

  const selectedTenant = tenants.find(t => t.id === selectedTenantId) || null;
  const selectedProject = projects.find(p => p.id === selectedProjectId) || null;

  // Trocar de projeto: grava e as páginas remontam (App usa o projeto como key)
  const setSelectedProjectId = (id: string) => {
    localStorage.setItem(PROJECT_STORAGE_KEY, id);
    setSelectedProjectIdState(id);
  };

  const reloadContext = async () => { await loadContext(); };

  const value: TenantContextType = {
    tenants,
    organizations,
    role,
    roleLabel,
    can,
    selectedTenantId,
    selectedTenant,
    setSelectedTenantId,
    loading,
    refreshTenants,
    projects,
    selectedProjectId,
    selectedProject,
    setSelectedProjectId,
    reloadContext,
    mfaSetupRequired,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant deve ser usado dentro de um TenantProvider');
  }
  return context;
}
